import {
  EDIT_AREA_COORDINATE_SYSTEM,
  FACE_KEYS,
  type FaceKey,
  MAX_COMPONENT_MM,
  MAX_COMPONENT_NAME_LENGTH,
  MAX_COMPONENTS,
  MAX_OUTLINE_POINTS,
  MAX_PLACEMENTS_PER_FACE,
  MIN_OUTLINE_POINTS,
  type ProductColorOption,
} from '@repo/api-contracts';
import type { EditAreaDraftState } from '@/entities/edit-area';
import { koreanMessageOr } from '@/shared/lib';

interface Issue {
  readonly path: readonly PropertyKey[];
  readonly message: string;
}
type IssueState = Pick<EditAreaDraftState, 'components' | 'faces' | 'draft'>;
type ColorLabels = readonly Pick<ProductColorOption, 'id' | 'name'>[];
interface IssueDescription {
  readonly key: string;
  readonly label: string;
  readonly message: string;
  readonly targetId: string;
}
interface Location {
  readonly identity: string;
  readonly label: string;
  readonly targetId: string;
  readonly hint: string;
}

const FACE_LABELS: Readonly<Record<FaceKey, string>> = {
  front: '앞면',
  back: '뒷면',
  left: '왼쪽 면',
  right: '오른쪽 면',
};
const FIELD_LABELS: Readonly<Record<string, string>> = {
  name: '이름',
  widthMm: '가로 (mm)',
  heightMm: '세로 (mm)',
  shape: '모양',
  holes: '인쇄 제외 영역',
  position: '위치 (mm)',
  xMm: 'X 위치 (mm)',
  yMm: 'Y 위치 (mm)',
  componentId: '인쇄 영역 선택',
  surfaceId: '표면 선택',
  colorOptionId: '상품 색상',
  photoKey: '사진',
  photoSize: '원본 크기',
  widthPx: '원본 가로 (px)',
  heightPx: '원본 세로 (px)',
  points: '사진 범위 (%)',
  x: '가로 좌표 (%)',
  y: '세로 좌표 (%)',
  realSize: '실측 크기 (mm)',
  outline: '제품 윤곽',
  topCurveRatio: '위 곡률',
  bottomCurveRatio: '아래 곡률',
  multiply: '사진에 곱하기(multiply)',
  maskKey: '마스크',
  maskSize: '마스크 크기',
  shadingKey: '음영',
  shadingSize: '음영 크기',
  safeAreaKey: '안내선',
  safeAreaSize: '안내선 크기',
};
const SIZE_HINT = `실측 크기는 0보다 크고 ${MAX_COMPONENT_MM} mm 이하여야 합니다.`;
const CURVE_HINT =
  '곡률은 기준 사진 px 로 입력하고, 위·아래 곡선의 양 끝이 사진 안에 있으며 서로 만나지 않아야 합니다.';
const COMPOSITE_HINT =
  '마스크는 원통 표면이 있는 면에 필수이고, 마스크·음영·안내선과 그 면의 모든 색상 사진은 같은 px 크기여야 합니다.';
const SURFACE_HINT = '면마다 표면은 1개만 둘 수 있습니다. 필수 값과 입력 형식을 확인해 주세요.';
const OUTLINE_HINT = `윤곽은 사진 안에서 스스로 교차하지 않는 닫힌 도형이어야 하며 점은 ${MIN_OUTLINE_POINTS}~${MAX_OUTLINE_POINTS}개입니다. '다시 추출하기'로 다시 잡아 주세요.`;
const POINTS_RANGE = EDIT_AREA_COORDINATE_SYSTEM.surfacePoints;
const POINTS_HINT_BASE = `사진 범위는 ${POINTS_RANGE.min}~${POINTS_RANGE.max}% 안의 축 정렬 직사각형이어야 하고`;
/** flat 은 4점(틀)이 윤곽을 모두 감싸야 하지만 cylinder 는 윤곽과 독립이고 곡선 끝까지 사진 안이어야 한다(ADR-0007 D7). */
const POINTS_HINTS: Readonly<Record<'cylinder' | 'other', string>> = {
  cylinder: `${POINTS_HINT_BASE}, 위·아래 곡선 끝까지 포함한 세로 범위가 사진 안에 있어야 합니다.`,
  other: `${POINTS_HINT_BASE}, 윤곽이 있으면 윤곽을 모두 감싸야 합니다.`,
};
const SHAPE_HINT = '자유 다각형은 캔버스에서 다시 그려 주세요. 원형은 가로·세로만 입력합니다.';
/** 인쇄 영역 입력 칸(이름·가로·세로·모양)의 한국어 안내. 저장 전 확인 목록과 인쇄 영역 폼이 같은 문구를 쓴다. */
export const componentFieldHint = (field: PropertyKey | undefined): string => {
  if (field === 'name') return `인쇄 영역 이름을 1~${MAX_COMPONENT_NAME_LENGTH}자로 입력해 주세요.`;
  if (field === 'shape' || field === 'points' || field === 'holes') return SHAPE_HINT;
  return SIZE_HINT;
};
const appendField = (label: string, field: PropertyKey | undefined): string =>
  field !== undefined ? `${label} · ${FIELD_LABELS[String(field)] ?? '입력 값'}` : label;
const indexOf = (value: PropertyKey | undefined): number | undefined =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : undefined;
const faceOf = (value: PropertyKey | undefined): FaceKey | undefined =>
  FACE_KEYS.find((face) => face === value);

const componentLocation = (path: readonly PropertyKey[], state: IssueState): Location => {
  const index = indexOf(path[1]);
  const component = index === undefined ? undefined : state.components[index];
  const label =
    component?.name ?? (index === undefined ? '인쇄 영역 목록' : `인쇄 영역 ${index + 1}`);
  // shape.points·shape.holes 는 한 단계 더 들어간 경로라 모양 안쪽 필드로 올려 본다.
  const field = path[2] === 'shape' && path[3] !== undefined ? path[3] : path[2];
  return {
    identity: `component:${component?.id ?? index ?? 'list'}:${path.slice(2).map(String).join(':')}`,
    label: field === 'points' ? `${label} · 모양 점 (mm)` : appendField(label, field),
    targetId: 'edit-area-components',
    hint:
      index === undefined
        ? `인쇄 영역은 1개 이상, 최대 ${MAX_COMPONENTS}개까지 정의해 주세요.`
        : componentFieldHint(field),
  };
};

interface FaceContext {
  readonly face: FaceKey | undefined;
  readonly name: string;
  readonly definition: EditAreaDraftState['faces'][FaceKey] | undefined;
}

const placementLocation = (
  path: readonly PropertyKey[],
  state: IssueState,
  { face, name, definition }: FaceContext,
): Location => {
  const index = indexOf(path[3]);
  const placement = index === undefined ? undefined : definition?.placements[index];
  const component = state.components.find((item) => item.id === placement?.componentId);
  const label =
    index === undefined
      ? `${name} · 배치 목록`
      : `${name} · ${component?.name ?? `인쇄 영역 ${index + 1}`} 배치`;
  return {
    identity: `placement:${face}:${placement?.componentId ?? index ?? 'list'}:${path.slice(4).map(String).join(':')}`,
    label: appendField(label, path[5] ?? path[4]),
    targetId: 'edit-area-placements',
    hint:
      index === undefined
        ? `한 면에는 최대 ${MAX_PLACEMENTS_PER_FACE}개의 인쇄 영역을 배치할 수 있습니다.`
        : '같은 면의 표면과 인쇄 영역을 선택하고 배치가 표면 실측 범위와 제품 윤곽 안에 있는지 확인해 주세요.',
  };
};

/** `faces.<면>.composite[.필드]` — '면 공통 합성 에셋' 줄(composite-asset-upload.tsx 의 section id)로 보낸다. */
const compositeLocation = (
  path: readonly PropertyKey[],
  { face, name }: FaceContext,
): Location => ({
  identity: `composite:${face}:${path.slice(3).map(String).join(':')}`,
  label: appendField(`${name} · 합성 에셋`, path[3]),
  targetId: 'edit-area-composite',
  hint: COMPOSITE_HINT,
});

const surfaceFieldHint = (
  field: PropertyKey | undefined,
  surfaceType: string | undefined,
): string => {
  if (field === 'realSize') return SIZE_HINT;
  if (field === 'points') return POINTS_HINTS[surfaceType === 'cylinder' ? 'cylinder' : 'other'];
  if (field === 'outline') return OUTLINE_HINT;
  if (field === 'topCurveRatio' || field === 'bottomCurveRatio') return CURVE_HINT;
  return SURFACE_HINT;
};

const surfaceLocation = (
  path: readonly PropertyKey[],
  { face, name, definition }: FaceContext,
): Location => {
  const index = indexOf(path[3]);
  const surface = index === undefined ? undefined : definition?.surfaces[index];
  const base = index === undefined ? `${name} · 표면 목록` : `${name} · 표면 ${index + 1}`;
  const field = path[4];
  let label: string;
  if (field === 'realSize')
    label =
      path[5] === undefined
        ? `${base} · 실측 크기 (mm)`
        : `${base} · 실측 ${FIELD_LABELS[String(path[5])] ?? '입력 값'}`;
  else if (field === 'points') label = `${base} · 사진 범위 (%)`;
  else if (field === 'outline') label = `${base} · 제품 윤곽`;
  else label = appendField(base, field);
  return {
    identity: `surface:${face}:${surface?.id ?? index ?? 'list'}:${path.slice(4).map(String).join(':')}`,
    label,
    targetId: 'edit-area-surfaces',
    hint: surfaceFieldHint(field, surface?.type),
  };
};

const faceLocation = (path: readonly PropertyKey[], state: IssueState): Location => {
  const face = faceOf(path[1]);
  const context: FaceContext = {
    face,
    name: face === undefined ? '상품 면' : FACE_LABELS[face],
    definition: face === undefined ? undefined : state.faces[face],
  };
  if (path[2] === 'placements') return placementLocation(path, state, context);
  if (path[2] === 'composite') return compositeLocation(path, context);
  return surfaceLocation(path, context);
};

/**
 * `colors.N.faces.<면>[.photoSize…]` — 그 색상·면의 사진 카드(face-photo-card.tsx 의 article id)로 보낸다.
 * 카드는 상품 색상 목록에 있는 색상 × 사용 중인 면에만 그려지므로, 그 밖이면 '상품 목업 이미지 관리' 구간으로 보낸다.
 */
const photoTargetId = (
  colorOption: ColorLabels[number] | undefined,
  face: FaceKey | undefined,
  state: IssueState,
): string =>
  colorOption !== undefined && face !== undefined && state.draft.activeFaces.includes(face)
    ? `edit-area-photo-${colorOption.id}-${face}`
    : 'edit-area-photos';

const colorLocation = (
  path: readonly PropertyKey[],
  state: IssueState,
  colors: ColorLabels,
): Location => {
  const index = indexOf(path[1]);
  const color = index === undefined ? undefined : state.draft.colors[index];
  const colorOption = colors.find((item) => item.id === color?.colorOptionId);
  const name = colorOption?.name ?? (index === undefined ? '상품 색상 사진' : `색상 ${index + 1}`);
  const face = faceOf(path[3]);
  const label = face === undefined ? name : `${name} · ${FACE_LABELS[face]} 사진`;
  return {
    identity: `photo:${color?.colorOptionId ?? index ?? 'list'}:${path.slice(2).map(String).join(':')}`,
    label: appendField(label, path[5] ?? path[4]),
    targetId: photoTargetId(colorOption, face, state),
    hint:
      face === undefined
        ? '모든 상품 색상에 앞면 사진과 사용 중인 면의 사진을 준비해 주세요.'
        : '이 면의 사진을 업로드하고 준비 상태를 확인해 주세요.',
  };
};

/** Resolve array positions against the current draft; stable IDs stay internal to React keys. */
export const describeEditAreaIssue = (
  issue: Issue,
  state: IssueState,
  colors: ColorLabels = [],
): IssueDescription => {
  const path = issue.path[0] === 'definition' ? issue.path.slice(1) : issue.path;
  let location: Location;
  if (path[0] === 'components') location = componentLocation(path, state);
  else if (path[0] === 'faces') location = faceLocation(path, state);
  else if (path[0] === 'colors') location = colorLocation(path, state, colors);
  else
    location = {
      identity: 'save',
      label: '전체 저장',
      targetId: 'edit-area-save',
      hint: '필수 값과 입력 형식이 올바른지 확인해 주세요.',
    };
  const message = koreanMessageOr(issue.message, location.hint);
  return {
    key: `${location.identity}:${message}`,
    label: location.label,
    targetId: location.targetId,
    message,
  };
};
